import { toast } from "src/shadcn/ui/use-toast";
import { useCentralAPI, UserCacheType } from "./centralAPI";
import { useProjectsAPI } from "./central.projectsAPI";
import { useActiveProjectTeamsIterationsAPI } from "./centralAPIActiveProjectTeamsIterations";
import { useActiveProjectTeamsAPI } from "./centralAPIActiveProjectTeams";
import { useQuery } from "@tanstack/react-query";
import { Iteration, Project, Team } from "src/types";
import { useRestAPI } from "./axiosAPI";
import { useAzureApis } from "./azureAPIs";



export function useUsersAPI() {

    const {
        centralData,
        isFetching,
        isFetched,
        isError,
        error
    } = useCentralAPI({
        trigger: false
    });




    const {
        getProjectsByUserId
    } = useProjectsAPI()


    type props = {
        filters?: {
            organizationIds?: string[],
            workItems?: 'completed' | 'inProgress',
        },
        sortBy?: 'uncompletedFirst' | 'completedFirst' | 'sortByName'
    }


    const listUsers = ({
        filters = {
            organizationIds: undefined,
            workItems: undefined
        },
        sortBy = 'completedFirst'
    }: props) => {

        const Users = centralData?.Users || []


        if (!Users) return []

        const filteredUsers = Users.filter((user) => {
            if (!filters.organizationIds || user.organizations.some(orgId => filters.organizationIds?.includes(orgId.id))) {

                const pendingWorkItems = user.projects.reduce((total, project) => total + project.totalWorkItem - project.completedWorkItem, 0)
                if (filters.workItems === 'completed' && pendingWorkItems > 0) {
                    return false
                }

                if (filters.workItems === 'inProgress' && pendingWorkItems === 0) {
                    return false
                }
                return true
            }

            return false

        })

        let sortedUsers = []

        if (sortBy === 'completedFirst') {

            sortedUsers = filteredUsers.sort((a, b) => {
                const aPendingWorkItems = a.projects.reduce((total, project) => total + project.totalWorkItem - project.completedWorkItem, 0)
                const bPendingWorkItems = b.projects.reduce((total, project) => total + project.totalWorkItem - project.completedWorkItem, 0)
                return aPendingWorkItems - bPendingWorkItems
            })

        }

        if (sortBy === 'uncompletedFirst') {

            sortedUsers = filteredUsers.sort((a, b) => {
                const aPendingWorkItems = a.projects.reduce((total, project) => total + project.totalWorkItem - project.completedWorkItem, 0)
                const bPendingWorkItems = b.projects.reduce((total, project) => total + project.totalWorkItem - project.completedWorkItem, 0)
                return bPendingWorkItems - aPendingWorkItems
            })

        }

        if (sortBy === 'sortByName') {

            sortedUsers = filteredUsers.sort((a, b) => {
                return a.user.displayName.localeCompare(b.user.displayName)
            })
        }

        const userWithProjects = filteredUsers.map(
            user => {
                const projects = getProjectsByUserId({
                    userOriginId: user.user.originId
                })


                return { ...user, projects }
            }
        )


        return userWithProjects

    }

    const getUserById = ({
        id,
        filters = {
            organizationIds: undefined,
            workItems: undefined
        },
        sortBy = 'completedFirst'
    }:{id:string}&props) => {
        const Users = centralData?.Users?.filter(user => user.id.includes(id))
        if (!Users) return null

        const filteredUsers = Users.filter((user) => {
            if (!filters.organizationIds || user.organizations.some(orgId => filters.organizationIds?.includes(orgId.id))) {

                const pendingWorkItems = user.projects.reduce((total, project) => total + project.totalWorkItem - project.completedWorkItem, 0)
                if (filters.workItems === 'completed' && pendingWorkItems > 0) {
                    return false
                }

                if (filters.workItems === 'inProgress' && pendingWorkItems === 0) {
                    return false
                }
                return true
            }

            return false

        })

        let sortedUsers = []

        if (sortBy === 'completedFirst') {

            sortedUsers = filteredUsers.sort((a, b) => {
                const aPendingWorkItems = a.projects.reduce((total, project) => total + project.totalWorkItem - project.completedWorkItem, 0)
                const bPendingWorkItems = b.projects.reduce((total, project) => total + project.totalWorkItem - project.completedWorkItem, 0)
                return aPendingWorkItems - bPendingWorkItems
            })

        }

        if (sortBy === 'uncompletedFirst') {

            sortedUsers = filteredUsers.sort((a, b) => {
                const aPendingWorkItems = a.projects.reduce((total, project) => total + project.totalWorkItem - project.completedWorkItem, 0)
                const bPendingWorkItems = b.projects.reduce((total, project) => total + project.totalWorkItem - project.completedWorkItem, 0)
                return bPendingWorkItems - aPendingWorkItems
            })

        }

        if (sortBy === 'sortByName') {

            sortedUsers = filteredUsers.sort((a, b) => {
                return a.user.displayName.localeCompare(b.user.displayName)
            })
        }

        const userWithProjects = filteredUsers.map(
            user => {
                const projects = getProjectsByUserId({
                    userOriginId: user.user.originId
                })


                return { ...user, projects }
            }
        )


        return userWithProjects[0]
    }




    return {
        listUsers,
        getUserById
    };
}



type props = {
    filters?: {
        organizationIds?: string[],
        workItems?: 'completed' | 'inProgress',
    },
    sortBy?: 'uncompletedFirst' | 'completedFirst' | 'sortByName',
    fromDate?: string,
    toDate?: string,
}


type WorkItemRelation = {
    rel: string | null;
    source: {
        id: number;
        url: string;
    } | null;
    target: {
        id: number;
        url: string;
    };
};

type WorkItemType = {
    id: number;
    rev: number;
    parentId: number | undefined;
    fields: {
        "System.Id": number;
        "System.WorkItemType": string;
        "System.State": string;
        "System.AssignedTo": {
            displayName: string;
            url: string;
            _links: {
                avatar: {
                    href: string;
                };
            };
            id: string;
            uniqueName: string;
            imageUrl: string;
            descriptor: string;
        };
        "System.CreatedBy": {
            displayName: string;
            url: string;
            _links: {
                avatar: {
                    href: string;
                };
            };
            id: string;
            uniqueName: string;
            imageUrl: string;
            descriptor: string;
        };
        "System.Title": string;
        "Microsoft.VSTS.Scheduling.RemainingWork": number;
    };
    url: string;
};

type WorkItemResponseType = {
    count: number;
    value: WorkItemType[];
};

export function useUsersCapacityAPI({
    filters = {
        organizationIds: undefined,
        workItems: undefined
    },
    sortBy = 'completedFirst',
    fromDate = '',
    toDate = '',
}: props) {


    const api = useRestAPI();
    const {
        centralData,
        isFetching,
        isFetched,
        isError,
        error
    } = useCentralAPI({
        trigger: false
    });


    const {
        getProjectsByUserId
    } = useProjectsAPI()





    const {
        ActiveProjectTeamsIterations,
        isFetching: isFetchingActiveProjectTeamsIterations,
        isError: isErrorActiveProjectTeamsIterations,
        isLoading: isLoadingActiveProjectTeamsIterations
    } = useActiveProjectTeamsIterationsAPI({
        trigger: true
    })

    const {
        ActiveProjectTeams,
        isFetching: isFetchingActiveProjectTeams,
        isError: isErrorActiveProjectTeams,
        isLoading: isLoadingActiveProjectTeams
    } = useActiveProjectTeamsAPI({
        trigger: true
    })

    const {
        loadWorkItemsDetails
    } = useAzureApis()

    type userCapacity = (UserCacheType & {
        activeProjects: (Project & {
            teams: (Team & {
                userCapacity: number;
                iteration?: (Iteration & {
                    workItemCompleted: WorkItemType[];
                    workItemUnCompleted: WorkItemType[];
                    totalWorkItemCount: number;
                })[] | undefined;
            })[]
            userTotalWorkItems: number;
            userCompletedWorkItems: number;
            totalTodayCapacity: number,
            totalPendingCapacityToday: number,
            totalCapacity: number
        })[],
        totalCapacityToday: number,
        totalPendingCapacityToday: number
    })[]




    async function loadIterationWorkItems(iteration: Iteration) {

        const organisation = centralData?.Organizations?.find(organization => organization.accountId === iteration.organization_accountId)

        //load work items ids
        const response = await api.get<{ workItemRelations: WorkItemRelation[]; }>({
            url: `https://dev.azure.com/${organisation?.accountName}/${iteration.projectId}/${iteration.teamId}/_apis/work/teamsettings/iterations/${iteration.id}/workitems`,
            mode: 'protected',
            params: {
                'api-version': '7.2-preview.1'
            },
        })

        if (response.success === false) {
            throw response.msg
        }

        const workItemIds = response.workItemRelations.map(workItemRelation => workItemRelation.target.id)

        if (!workItemIds?.length) {
            return []
        }
        //load work items details
        const response2 = await loadWorkItemsDetails({
            projectId: iteration.projectId,
            organisationName: organisation?.accountName || '',
            workItemIds,
            projectType: 'basic'
        })

        return response2
    }

    const {
        data: listUsersWithCapacity,
        isLoading: isLoadinglistUsersWithCapacity,
        isError: isErrorlistUsersWithCapacity,
        refetch: refetchlistUsersWithCapacity,
        isFetching: isFetchinglistUsersWithCapacity,
        isFetched: isFetchedlistUsersWithCapacity,
        error: errorlistUsersWithCapacity
    } = useQuery({
        queryKey: ['users-capacity', fromDate, toDate],
        queryFn: async (): Promise<userCapacity> => {

            const Users = centralData?.Users || []




            if (!Users) return []



            // load all active projects iterations work items

            const ActiveProjectTeamsIterationsWithWorkItems: (Iteration & { workItems: WorkItemType[] })[] = []

            //filter based on from date

            // Parse fromDate and toDate if provided, otherwise handle as needed
            const from = fromDate ? new Date(fromDate) : null;
            if (from) from.setHours(0, 0, 0, 0);  // Normalize to midnight if fromDate is provided

            const to = toDate ? new Date(toDate) : null;
            if (to) to.setHours(23, 59, 59, 999);  // Normalize to the end of the day if toDate is provided




            const ActiveProjectTeamsIterationsFilteredByDate = ActiveProjectTeamsIterations?.filter(item => {
                // Get the startDate and endDate of the item and parse them as Date objects
                const startDate = new Date(item.attributes.startDate);
                startDate.setHours(0, 0, 0, 0);  // Normalize to midnight

                const endDate = new Date(item.attributes.finishDate);
                endDate.setHours(23, 59, 59, 999);  // Normalize to the end of the day

                // Check if the sprint period intersects the fromDate and toDate range
                const isAfterFrom = from ? startDate <= (to ?? new Date()) && endDate >= (from ?? new Date()) : true;
                const isBeforeTo = to ? startDate <= (to ?? new Date()) && endDate >= (from ?? new Date()) : true;


                // Return true if the date falls within the range or if no range is specified
                return isAfterFrom && isBeforeTo;
            });


            if (ActiveProjectTeamsIterationsFilteredByDate) {

                await Promise.all(
                    ActiveProjectTeamsIterationsFilteredByDate.map(async (iteration) => {
                        const workItems = await loadIterationWorkItems(iteration)
                        ActiveProjectTeamsIterationsWithWorkItems.push({
                            ...iteration,
                            workItems
                        })
                    })
                )

            }


            let newUsers = Users.map(user => {

                let userActiveProjects = getProjectsByUserId({
                    userOriginId: user.user.originId,
                    filter: {
                        projectStatus: 'inProgress'
                    }
                });

                let userActiveProjectsWithTeams = userActiveProjects.map(project => {



                    const teams = ActiveProjectTeams?.filter(team => team.projectId === project.id)

                    const currentUserTeams = teams?.filter(team => team.usersIds.some(userId => user.id.includes(userId)))
                    const teamTotal = currentUserTeams?.map(
                        team => {
                            const iterations = ActiveProjectTeamsIterationsWithWorkItems
                                ?.filter(iteration => iteration.teamId === team.id)
                                .sort((a, b) => new Date(a.attributes.startDate).getTime() - new Date(b.attributes.startDate).getTime())
                            //find capacity of this user in this iteration
                            // const userCapacity = iterations?.capacity.teamMembers.find(capacity => user.id.includes(capacity.teamMember.id))?.activities.reduce((acc, curr) => acc + curr.capacityPerDay, 0) || 0
                            const teamWithIterations = iterations?.map(iteration => {
                                const userCapacity = iteration?.capacity.teamMembers.find(capacity => user.id.includes(capacity.teamMember.id))?.activities.reduce((acc, curr) => acc + curr.capacityPerDay, 0) || 0;
                                //completed work item of this user
                                const workItemCompleted = iteration?.workItems?.filter(workItem => user.id.includes(workItem.fields['System.AssignedTo']?.id)).filter(workItem => workItem.fields['System.State'] === 'Done')
                                //un completed work item
                                const workItemUnCompleted = iteration?.workItems?.filter(workItem => user.id.includes(workItem.fields['System.AssignedTo']?.id)).filter(workItem => workItem.fields['System.State'] !== 'Done')
                                return {
                                    userCapacity,
                                    iteration,
                                    workItemCompleted,
                                    workItemUnCompleted,
                                    totalWorkItemCount: workItemCompleted.length + workItemUnCompleted.length
                                }
                            }) || []

                            const userCapacity = teamWithIterations.reduce((acc, curr) => acc + curr.userCapacity, 0)

                            return {
                                ...team,
                                iterations: teamWithIterations,
                                userCapacity: userCapacity
                            }


                        }
                    )

                    let totalTodayCapacity = 0
                    let totalTodayPendingCapacityToday = 0;

                    const todaysIterations = teamTotal?.filter(team => team.iterations.length ? true : false).filter(
                        team => {

                            team.iterations.forEach(iteration => {
                                if (iteration.iteration.attributes.timeFrame === 'current' && iteration?.iteration?.attributes.startDate && iteration?.iteration?.attributes.finishDate) {
                                    const startDate = new Date(iteration.iteration?.attributes.startDate)
                                    const finishDate = new Date(iteration.iteration?.attributes.finishDate)
                                    const today = new Date()
                                    // Reset the time part to midnight (00:00:00) for accurate date comparison
                                    startDate.setHours(0, 0, 0, 0);
                                    finishDate.setHours(23, 59, 59, 999);
                                    today.setHours(0, 0, 0, 0);
                                    if (today >= startDate && today <= finishDate) {
                                        totalTodayCapacity += iteration.userCapacity
                                        return true
                                    }
                                }
                                if (iteration.iteration.attributes.timeFrame === 'past' || iteration.iteration.attributes.timeFrame === 'current' && iteration?.iteration?.attributes.startDate && iteration?.iteration?.attributes.finishDate) {

                                    const finishDate = new Date(iteration.iteration?.attributes.finishDate)
                                    const today = new Date()
                                    // Reset the time part to midnight (00:00:00) for accurate date comparison
                                    finishDate.setHours(23, 59, 59, 999);
                                    today.setHours(0, 0, 0, 0);
                                    if (today > finishDate && iteration.workItemUnCompleted.length > 0) {
                                        totalTodayPendingCapacityToday += iteration.userCapacity
                                        return true
                                    }
                                    return true
                                }
                            })

                            return false
                        }
                    )

                    // const totalTodayCapacity = todaysIterations?.reduce((acc, curr) => acc + curr?.iterations[0]?.userCapacity, 0)
                    const totalCapcity = teamTotal?.reduce((acc, curr) => acc + (curr?.iterations?.reduce((acc1, curr1) => acc1 + curr1.userCapacity, 0) || 0), 0)

                    return {
                        ...project,
                        teams: teamTotal || [],
                        totalTodayCapacity: totalTodayCapacity || 0,
                        totalCapacity: totalCapcity || 0,
                        totalPendingCapacityToday: totalTodayPendingCapacityToday
                    }
                })


                //list the iteration in total project within current date
                let totalCapacityOfuserToday = 0
                let totalPendingCapacityToday = 0

                const todaysIterations = userActiveProjectsWithTeams.forEach(
                    project => {

                        totalCapacityOfuserToday += project.totalTodayCapacity
                        totalPendingCapacityToday += project.totalPendingCapacityToday
                        return false
                    }
                )


                // const totalCapacityOfuserToday = todaysIterations.reduce((acc, curr) => acc + curr.userCapacity, 0)

                return {
                    ...user,
                    activeProjects: userActiveProjectsWithTeams,
                    totalCapacityToday: totalCapacityOfuserToday,
                    totalPendingCapacityToday: totalPendingCapacityToday
                }
            })



            const filteredUsers = newUsers.filter((user) => {
                if (!filters.organizationIds || user.organizations.some(orgId => filters.organizationIds?.includes(orgId.id))) {

                    const pendingWorkItems = user.projects.reduce((total, project) => total + project.totalWorkItem - project.completedWorkItem, 0)
                    if (filters.workItems === 'completed' && pendingWorkItems > 0) {
                        return false
                    }

                    if (filters.workItems === 'inProgress' && pendingWorkItems === 0) {
                        return false
                    }
                    return true
                }

                return false

            })


            let sortedUsers = []

            if (sortBy === 'completedFirst') {

                sortedUsers = filteredUsers.sort((a, b) => {
                    const aPendingWorkItems = a.projects.reduce((total, project) => total + project.totalWorkItem - project.completedWorkItem, 0)
                    const bPendingWorkItems = b.projects.reduce((total, project) => total + project.totalWorkItem - project.completedWorkItem, 0)
                    return aPendingWorkItems - bPendingWorkItems
                })

            }

            if (sortBy === 'uncompletedFirst') {

                sortedUsers = filteredUsers.sort((a, b) => {
                    const aPendingWorkItems = a.projects.reduce((total, project) => total + project.totalWorkItem - project.completedWorkItem, 0)
                    const bPendingWorkItems = b.projects.reduce((total, project) => total + project.totalWorkItem - project.completedWorkItem, 0)
                    return bPendingWorkItems - aPendingWorkItems
                })

            }

            if (sortBy === 'sortByName') {

                sortedUsers = filteredUsers.sort((a, b) => {
                    return a.user.displayName.localeCompare(b.user.displayName)
                })
            }

            const userWithProjects = filteredUsers.map(
                user => {


                    return { ...user }
                }
            )

            console.log(userWithProjects)

            return userWithProjects

        },
        refetchOnWindowFocus: false,
        enabled: !(isFetchingActiveProjectTeams || isFetchingActiveProjectTeamsIterations)

    })

    return {
        listUsersWithCapacity,
        isFetching: isFetchingActiveProjectTeams || isFetchingActiveProjectTeamsIterations || isFetchinglistUsersWithCapacity,
        isLoading: isLoadingActiveProjectTeams || isLoadingActiveProjectTeamsIterations || isLoadinglistUsersWithCapacity
    }

}



export function useUserCapacityAPI(
    {
        filters = {
            organizationIds: undefined,
            workItems: undefined
        },
        sortBy = 'completedFirst',
        fromDate = '',
        toDate = '',
        userOriginId
    }: props & {
        userOriginId: string
    }
) {


    const api = useRestAPI();
    const {
        centralData,
        isFetching,
        isFetched,
        isError,
        error
    } = useCentralAPI({
        trigger: false
    });

    const {
        loadWorkItemsDetails
    } = useAzureApis()


    const {
        getProjectsByUserId
    } = useProjectsAPI()



    const {
        ActiveProjectTeamsIterations,
        isFetching: isFetchingActiveProjectTeamsIterations,
        isError: isErrorActiveProjectTeamsIterations,
        isLoading: isLoadingActiveProjectTeamsIterations
    } = useActiveProjectTeamsIterationsAPI({
        trigger: true
    })

    const {
        ActiveProjectTeams,
        isFetching: isFetchingActiveProjectTeams,
        isError: isErrorActiveProjectTeams,
        isLoading: isLoadingActiveProjectTeams
    } = useActiveProjectTeamsAPI({
        trigger: true
    })

    type userCapacity = (UserCacheType & {
        activeProjects: (Project & {
            teams: (Team & {
                userCapacity: number;
                iteration?: (Iteration & {
                    workItemCompleted: WorkItemType[];
                    workItemUnCompleted: WorkItemType[];
                    totalWorkItemCount: number;
                })[] | undefined;
            })[]
            userTotalWorkItems: number;
            userCompletedWorkItems: number;
            totalTodayCapacity: number,
            totalPendingCapacityToday: number,
            totalCapacity: number
        })[],
        totalCapacityToday: number,
        totalPendingCapacityToday: number
    })




    async function loadIterationWorkItems(iteration: Iteration) {

        const organisation = centralData?.Organizations?.find(organization => organization.accountId === iteration.organization_accountId)

        //load work items ids
        const response = await api.get<{ workItemRelations: WorkItemRelation[]; }>({
            url: `https://dev.azure.com/${organisation?.accountName}/${iteration.projectId}/${iteration.teamId}/_apis/work/teamsettings/iterations/${iteration.id}/workitems`,
            mode: 'protected',
            params: {
                'api-version': '7.2-preview.1'
            },
        })

        if (response.success === false) {
            throw response.msg
        }

        const workItemIds = response.workItemRelations.map(workItemRelation => workItemRelation.target.id)

        if (!workItemIds?.length) {
            return []
        }
        //load work items details
        const response2 = await loadWorkItemsDetails({
            projectId: iteration.projectId,
            organisationName: organisation?.accountName || '',
            workItemIds,
            projectType: 'basic'
        })

        return response2
    }


    const {
        data: UserWithCapacity,
        isLoading: isLoadinglistUsersWithCapacity,
        isError: isErrorlistUsersWithCapacity,
        refetch: refetchlistUsersWithCapacity,
        isFetching: isFetchinglistUsersWithCapacity,
        isFetched: isFetchedlistUsersWithCapacity,
        error: errorlistUsersWithCapacity
    } = useQuery({
        queryKey: ['user-capacity', fromDate, toDate, userOriginId],
        queryFn: async (): Promise<userCapacity | undefined> => {

            const User = centralData?.Users.find(user => user.user.originId === userOriginId)




            if (!User) return



            // load all active projects iterations work items

            const ActiveProjectTeamsIterationsWithWorkItems: (Iteration & { workItems: WorkItemType[] })[] = []


            //active projects of user
            const activeProjectsOfUser = getProjectsByUserId({
                userOriginId: userOriginId,
            }).filter(project => project.projectStatus === 'inProgress')

            //filter based on from date

            // Parse fromDate and toDate if provided, otherwise handle as needed
            const from = fromDate ? new Date(fromDate) : null;
            if (from) from.setHours(0, 0, 0, 0);  // Normalize to midnight if fromDate is provided

            const to = toDate ? new Date(toDate) : null;
            if (to) to.setHours(23, 59, 59, 999);  // Normalize to the end of the day if toDate is provided




            const ActiveProjectTeamsIterationsFilteredByDate = ActiveProjectTeamsIterations?.filter(item => {
                // Get the startDate and endDate of the item and parse them as Date objects
                const startDate = new Date(item.attributes.startDate);
                startDate.setHours(0, 0, 0, 0);  // Normalize to midnight

                const endDate = new Date(item.attributes.finishDate);
                endDate.setHours(23, 59, 59, 999);  // Normalize to the end of the day

                // Check if the sprint period intersects the fromDate and toDate range
                const isAfterFrom = from ? startDate <= (to ?? new Date()) && endDate >= (from ?? new Date()) : true;
                const isBeforeTo = to ? startDate <= (to ?? new Date()) && endDate >= (from ?? new Date()) : true;

                // Check if the iteration belongs to the current user
                const isCurrentUserIteration = activeProjectsOfUser.some(project => project.id === item.projectId);

                // Return true if the sprint period intersects the range and the project belongs to the current user
                return isAfterFrom && isBeforeTo && isCurrentUserIteration;
            });


            if (ActiveProjectTeamsIterationsFilteredByDate) {

                await Promise.all(
                    ActiveProjectTeamsIterationsFilteredByDate.map(async (iteration) => {
                        const workItems = await loadIterationWorkItems(iteration)
                        ActiveProjectTeamsIterationsWithWorkItems.push({
                            ...iteration,
                            workItems
                        })
                    })
                )

            }




            let userActiveProjects = getProjectsByUserId({
                userOriginId: User.user.originId,
                filter: {
                    projectStatus: 'inProgress'
                }
            });

            let userActiveProjectsWithTeams = userActiveProjects.map(project => {



                const teams = ActiveProjectTeams?.filter(team => team.projectId === project.id)

                const currentUserTeams = teams?.filter(team => team.usersIds.some(userId => User.id.includes(userId)))
                const teamTotal = currentUserTeams?.map(
                    team => {
                        const iterations = ActiveProjectTeamsIterationsWithWorkItems
                            ?.filter(iteration => iteration.teamId === team.id)
                            .sort((a, b) => new Date(a.attributes.startDate).getTime() - new Date(b.attributes.startDate).getTime())
                        //find capacity of this user in this iteration
                        // const userCapacity = iterations?.capacity.teamMembers.find(capacity => user.id.includes(capacity.teamMember.id))?.activities.reduce((acc, curr) => acc + curr.capacityPerDay, 0) || 0
                        const teamWithIterations = iterations?.map(iteration => {
                            const userCapacity = iteration?.capacity.teamMembers.find(capacity => User.id.includes(capacity.teamMember.id))?.activities.reduce((acc, curr) => acc + curr.capacityPerDay, 0) || 0;
                            //completed work item of this user
                            const workItemCompleted = iteration?.workItems?.filter(workItem => User.id.includes(workItem.fields['System.AssignedTo']?.id)).filter(workItem => workItem.fields['System.State'] === 'Done')
                            //un completed work item
                            const workItemUnCompleted = iteration?.workItems?.filter(workItem => User.id.includes(workItem.fields['System.AssignedTo']?.id)).filter(workItem => workItem.fields['System.State'] !== 'Done')
                            return {
                                userCapacity,
                                iteration,
                                workItemCompleted,
                                workItemUnCompleted,
                                totalWorkItemCount: workItemCompleted.length + workItemUnCompleted.length
                            }
                        }) || []

                        const userCapacity = teamWithIterations.reduce((acc, curr) => acc + curr.userCapacity, 0)

                        return {
                            ...team,
                            iterations: teamWithIterations,
                            userCapacity: userCapacity
                        }


                    }
                )

                let totalTodayCapacity = 0
                let totalTodayPendingCapacityToday = 0;

                const todaysIterations = teamTotal?.filter(team => team.iterations.length ? true : false).filter(
                    team => {

                        team.iterations.forEach(iteration => {
                            if (iteration.iteration.attributes.timeFrame === 'current' && iteration?.iteration?.attributes.startDate && iteration?.iteration?.attributes.finishDate) {
                                const startDate = new Date(iteration.iteration?.attributes.startDate)
                                const finishDate = new Date(iteration.iteration?.attributes.finishDate)
                                const today = new Date()
                                // Reset the time part to midnight (00:00:00) for accurate date comparison
                                startDate.setHours(0, 0, 0, 0);
                                finishDate.setHours(23, 59, 59, 999);
                                today.setHours(0, 0, 0, 0);
                                if (today >= startDate && today <= finishDate) {
                                    totalTodayCapacity += iteration.userCapacity
                                    return true
                                }
                            }
                            if (iteration.iteration.attributes.timeFrame === 'past' || iteration.iteration.attributes.timeFrame === 'current' && iteration?.iteration?.attributes.startDate && iteration?.iteration?.attributes.finishDate) {

                                const finishDate = new Date(iteration.iteration?.attributes.finishDate)
                                const today = new Date()
                                // Reset the time part to midnight (00:00:00) for accurate date comparison
                                finishDate.setHours(23, 59, 59, 999);
                                today.setHours(0, 0, 0, 0);
                                if (today > finishDate && iteration.workItemUnCompleted.length > 0) {
                                    totalTodayPendingCapacityToday += iteration.userCapacity
                                    return true
                                }
                                return true
                            }
                        })

                        return false
                    }
                )

                // const totalTodayCapacity = todaysIterations?.reduce((acc, curr) => acc + curr?.iterations[0]?.userCapacity, 0)
                const totalCapcity = teamTotal?.reduce((acc, curr) => acc + (curr?.iterations?.reduce((acc1, curr1) => acc1 + curr1.userCapacity, 0) || 0), 0)

                return {
                    ...project,
                    teams: teamTotal || [],
                    totalTodayCapacity: totalTodayCapacity || 0,
                    totalCapacity: totalCapcity || 0,
                    totalPendingCapacityToday: totalTodayPendingCapacityToday
                }
            })


            //list the iteration in total project within current date
            let totalCapacityOfuserToday = 0
            let totalPendingCapacityToday = 0

            const todaysIterations = userActiveProjectsWithTeams.forEach(
                project => {

                    totalCapacityOfuserToday += project.totalTodayCapacity
                    totalPendingCapacityToday += project.totalPendingCapacityToday
                    return false
                }
            )


            // const totalCapacityOfuserToday = todaysIterations.reduce((acc, curr) => acc + curr.userCapacity, 0)

            const newUser = {
                ...User,
                activeProjects: userActiveProjectsWithTeams,
                totalCapacityToday: totalCapacityOfuserToday,
                totalPendingCapacityToday: totalPendingCapacityToday
            }




            const filteredUsers = newUser



            return filteredUsers

        },
        refetchOnWindowFocus: false,
        enabled: !(isFetchingActiveProjectTeams || isFetchingActiveProjectTeamsIterations)

    })
    console.log(isLoadinglistUsersWithCapacity)

    return {
        UserWithCapacity,
        isFetching: isFetchingActiveProjectTeams || isFetchingActiveProjectTeamsIterations || isFetchinglistUsersWithCapacity,
        isLoading: isLoadingActiveProjectTeams || isLoadingActiveProjectTeamsIterations || isLoadinglistUsersWithCapacity
    }

}


export function useUserWorkItemsAPI(
    {
        filters = {
            organizationIds: undefined,
            workItems: undefined
        },
        sortBy = 'completedFirst',
        fromDate = '',
        toDate = '',
        userOriginId
    }: props & {
        userOriginId: string
    }
) {


    const api = useRestAPI();
    const {
        loadWorkItemsDetails
    } = useAzureApis()
    const {
        centralData,
        isFetching,
        isFetched,
        isError,
        error
    } = useCentralAPI({
        trigger: false
    });


    const {
        getProjectsByUserId
    } = useProjectsAPI()



    const {
        ActiveProjectTeamsIterations,
        isFetching: isFetchingActiveProjectTeamsIterations,
        isError: isErrorActiveProjectTeamsIterations,
        isLoading: isLoadingActiveProjectTeamsIterations
    } = useActiveProjectTeamsIterationsAPI({
        trigger: true
    })

    const {
        ActiveProjectTeams,
        isFetching: isFetchingActiveProjectTeams,
        isError: isErrorActiveProjectTeams,
        isLoading: isLoadingActiveProjectTeams
    } = useActiveProjectTeamsAPI({
        trigger: true
    })

    type userActiveProjectsWithWorkItems = (UserCacheType & {
        activeProjects: (Project & {
            teams: (Team & {
                userCapacity: number;
                iteration?: (Iteration & {
                    workItemCompleted: WorkItemType[];
                    workItemUnCompleted: WorkItemType[];
                    totalWorkItemCount: number;
                    totalWorkItemsDetails: (WorkItemType & { parentId: number | null })[]
                })[] | undefined;
            })[]
            userTotalWorkItems: number;
            userCompletedWorkItems: number;
            totalTodayCapacity: number,
            totalPendingCapacityToday: number,
            totalCapacity: number
        })[],
        totalCapacityToday: number,
        totalPendingCapacityToday: number
    })




    async function loadIterationWorkItems(iteration: Iteration) {

        const organisation = centralData?.Organizations?.find(organization => organization.accountId === iteration.organization_accountId)

        //load work items ids
        const response = await api.get<{ workItemRelations: WorkItemRelation[]; }>({
            url: `https://dev.azure.com/${organisation?.accountName}/${iteration.projectId}/${iteration.teamId}/_apis/work/teamsettings/iterations/${iteration.id}/workitems`,
            mode: 'protected',
            params: {
                'api-version': '7.2-preview.1'
            },
        })

        if (response.success === false) {
            throw response.msg
        }

        const workItemIds = response.workItemRelations.map(workItemRelation => workItemRelation.target.id)

        if (!workItemIds?.length) {
            return []
        }
        //load work items details
        //load work items details
        const response2 = await loadWorkItemsDetails({
            projectId: iteration.projectId,
            organisationName: organisation?.accountName || '',
            workItemIds,
            projectType: 'basic'
        })

        //adding parent id with work item
        const workItemsWithParentId = response2.map(workItem => {

            const workrelation = response.workItemRelations.find(workItemRelation => workItemRelation.target.id === workItem.id)

            return ({
                ...workItem,
                parentId: workrelation?.source ? workrelation.source.id : undefined
            })
        })

        return workItemsWithParentId
    }


    const {
        data: UserWithCapacity,
        isLoading: isLoadinglistUsersWithCapacity,
        isError: isErrorlistUsersWithCapacity,
        refetch: refetchlistUsersWithCapacity,
        isFetching: isFetchinglistUsersWithCapacity,
        isFetched: isFetchedlistUsersWithCapacity,
        error: errorlistUsersWithCapacity
    } = useQuery({
        queryKey: ['user-my-work-items', fromDate, toDate, userOriginId],
        queryFn: async (): Promise<userActiveProjectsWithWorkItems | undefined> => {

            const User = centralData?.Users.find(user => user.user.originId === userOriginId)




            if (!User) return


            // load all active projects iterations work items

            const ActiveProjectTeamsIterationsWithWorkItems: (Iteration & { workItems: WorkItemType[] })[] = []


            //active projects of user
            const activeProjectsOfUser = getProjectsByUserId({
                userOriginId: userOriginId,
            }).filter(project => project.projectStatus === 'inProgress')

            //filter based on from date

            // Parse fromDate and toDate if provided, otherwise handle as needed
            const from = fromDate ? new Date(fromDate) : null;
            if (from) from.setHours(0, 0, 0, 0);  // Normalize to midnight if fromDate is provided

            const to = toDate ? new Date(toDate) : null;
            if (to) to.setHours(23, 59, 59, 999);  // Normalize to the end of the day if toDate is provided




            const ActiveProjectTeamsIterationsFilteredByDate = ActiveProjectTeamsIterations?.filter(item => {
                // Get the startDate and endDate of the item and parse them as Date objects
                const startDate = new Date(item.attributes.startDate);
                startDate.setHours(0, 0, 0, 0);  // Normalize to midnight

                const endDate = new Date(item.attributes.finishDate);
                endDate.setHours(23, 59, 59, 999);  // Normalize to the end of the day

                // Check if the sprint period intersects the fromDate and toDate range
                const isAfterFrom = from ? startDate <= (to ?? new Date()) && endDate >= (from ?? new Date()) : true;
                const isBeforeTo = to ? startDate <= (to ?? new Date()) && endDate >= (from ?? new Date()) : true;

                // Check if the iteration belongs to the current user
                const isCurrentUserIteration = activeProjectsOfUser.some(project => project.id === item.projectId);

                // Return true if the sprint period intersects the range and the project belongs to the current user
                return isAfterFrom && isBeforeTo && isCurrentUserIteration;
            });


            if (ActiveProjectTeamsIterationsFilteredByDate) {

                await Promise.all(
                    ActiveProjectTeamsIterationsFilteredByDate.map(async (iteration) => {
                        const workItems = await loadIterationWorkItems(iteration)
                        ActiveProjectTeamsIterationsWithWorkItems.push({
                            ...iteration,
                            workItems
                        })
                    })
                )

            }




            let userActiveProjects = getProjectsByUserId({
                userOriginId: User.user.originId,
                filter: {
                    projectStatus: 'inProgress'
                }
            });

            let userActiveProjectsWithTeams = userActiveProjects.map(project => {



                const teams = ActiveProjectTeams?.filter(team => team.projectId === project.id)

                const currentUserTeams = teams?.filter(team => team.usersIds.some(userId => User.id.includes(userId)))
                const teamTotal = currentUserTeams?.map(
                    team => {
                        const iterations = ActiveProjectTeamsIterationsWithWorkItems
                            ?.filter(iteration => iteration.teamId === team.id)
                            .sort((a, b) => new Date(a.attributes.startDate).getTime() - new Date(b.attributes.startDate).getTime())
                        //find capacity of this user in this iteration
                        // const userCapacity = iterations?.capacity.teamMembers.find(capacity => user.id.includes(capacity.teamMember.id))?.activities.reduce((acc, curr) => acc + curr.capacityPerDay, 0) || 0
                        const teamWithIterations = iterations?.map(iteration => {
                            const userCapacity = iteration?.capacity.teamMembers.find(capacity => User.id.includes(capacity.teamMember.id))?.activities.reduce((acc, curr) => acc + curr.capacityPerDay, 0) || 0;
                            //completed work item of this user
                            const workItemCompleted = iteration?.workItems?.filter(workItem => User.id.includes(workItem.fields['System.AssignedTo']?.id)).filter(workItem => workItem.fields['System.State'] === 'Done')
                            //un completed work item
                            const workItemUnCompleted = iteration?.workItems?.filter(workItem => User.id.includes(workItem.fields['System.AssignedTo']?.id)).filter(workItem => workItem.fields['System.State'] !== 'Done')
                            const totalWorkItemsDetails = iteration?.workItems?.filter(workItem => User.id.includes(workItem.fields['System.AssignedTo']?.id))

                            //make parent id null if parent is not availble
                            const allParentIds = new Set(totalWorkItemsDetails.map(item => item.id));
                            totalWorkItemsDetails.forEach(item => {
                                // If the parentId is not found in the array of work items, set parentId to null
                                if (!allParentIds.has(item.parentId!)) {
                                    item.parentId = undefined;
                                }
                            });
                            return {
                                userCapacity,
                                iteration,
                                workItemCompleted,
                                workItemUnCompleted,
                                totalWorkItemsDetails,
                                totalWorkItemCount: workItemCompleted.length + workItemUnCompleted.length
                            }
                        }) || []


                        //filter only work item uncompleted iterations
                        const unCompletedIterations = teamWithIterations.filter(iteration => iteration.workItemUnCompleted.length > 0)


                        const userCapacity = unCompletedIterations.reduce((acc, curr) => acc + curr.userCapacity, 0)

                        return {
                            ...team,
                            iterations: unCompletedIterations,
                            userCapacity: userCapacity
                        }


                    }
                )

                let totalTodayCapacity = 0
                let totalTodayPendingCapacityToday = 0;

                const todaysIterations = teamTotal?.filter(team => team.iterations.length ? true : false).filter(
                    team => {

                        team.iterations.forEach(iteration => {
                            if (iteration.iteration.attributes.timeFrame === 'current' && iteration?.iteration?.attributes.startDate && iteration?.iteration?.attributes.finishDate) {
                                const startDate = new Date(iteration.iteration?.attributes.startDate)
                                const finishDate = new Date(iteration.iteration?.attributes.finishDate)
                                const today = new Date()
                                // Reset the time part to midnight (00:00:00) for accurate date comparison
                                startDate.setHours(0, 0, 0, 0);
                                finishDate.setHours(23, 59, 59, 999);
                                today.setHours(0, 0, 0, 0);
                                if (today >= startDate && today <= finishDate) {
                                    totalTodayCapacity += iteration.userCapacity
                                    return true
                                }
                            }
                            if (iteration.iteration.attributes.timeFrame === 'past' || iteration.iteration.attributes.timeFrame === 'current' && iteration?.iteration?.attributes.startDate && iteration?.iteration?.attributes.finishDate) {

                                const finishDate = new Date(iteration.iteration?.attributes.finishDate)
                                const today = new Date()
                                // Reset the time part to midnight (00:00:00) for accurate date comparison
                                finishDate.setHours(23, 59, 59, 999);
                                today.setHours(0, 0, 0, 0);
                                if (today > finishDate && iteration.workItemUnCompleted.length > 0) {
                                    totalTodayPendingCapacityToday += iteration.userCapacity
                                    return true
                                }
                                return true
                            }
                        })

                        return false
                    }
                )

                // const totalTodayCapacity = todaysIterations?.reduce((acc, curr) => acc + curr?.iterations[0]?.userCapacity, 0)
                const totalCapcity = teamTotal?.reduce((acc, curr) => acc + (curr?.iterations?.reduce((acc1, curr1) => acc1 + curr1.userCapacity, 0) || 0), 0)

                return {
                    ...project,
                    teams: teamTotal || [],
                    totalTodayCapacity: totalTodayCapacity || 0,
                    totalCapacity: totalCapcity || 0,
                    totalPendingCapacityToday: totalTodayPendingCapacityToday
                }
            })

            //remove all iteration with no team
            userActiveProjectsWithTeams = userActiveProjectsWithTeams.filter(project => project.teams.length > 0)
            //remove all project with no iteration in team
            userActiveProjectsWithTeams = userActiveProjectsWithTeams.filter(project => project.teams.some(team => team.iterations.length > 0))
            //also remove all team from active project with no iteration
            userActiveProjectsWithTeams.map(project => project.teams = project.teams.filter(team => team.iterations.length > 0))

            //list the iteration in total project within current date
            let totalCapacityOfuserToday = 0
            let totalPendingCapacityToday = 0

            const todaysIterations = userActiveProjectsWithTeams.forEach(
                project => {

                    totalCapacityOfuserToday += project.totalTodayCapacity
                    totalPendingCapacityToday += project.totalPendingCapacityToday
                    return false
                }
            )


            // const totalCapacityOfuserToday = todaysIterations.reduce((acc, curr) => acc + curr.userCapacity, 0)

            const newUser = {
                ...User,
                activeProjects: userActiveProjectsWithTeams,
                totalCapacityToday: totalCapacityOfuserToday,
                totalPendingCapacityToday: totalPendingCapacityToday
            }




            const filteredUsers = newUser

            console.log(filteredUsers)



            return filteredUsers

        },
        refetchOnWindowFocus: false,
        enabled: !(isFetchingActiveProjectTeams || isFetchingActiveProjectTeamsIterations)

    })
    console.log(isLoadinglistUsersWithCapacity)

    return {
        UserWithCapacity,
        isFetching: isFetchingActiveProjectTeams || isFetchingActiveProjectTeamsIterations || isFetchinglistUsersWithCapacity,
        isLoading: isLoadingActiveProjectTeams || isLoadingActiveProjectTeamsIterations || isLoadinglistUsersWithCapacity
    }

}
